import React, { useState, useEffect, useRef, useCallback } from 'react';
import { User, CVTemplate, UserRole, CVData, CVSection } from '../types';
import { supabase_get_pro_cv_templates, supabase, supabase_get_talent_pool, supabase_get_candidates_light, supabase_get_candidate_profile_by_id, supabase_get_paginated_talent_pool, supabase_get_vacancies, supabase_save_document } from '../services/supabaseService';
import { ChevronLeft, FileText, Loader2, Maximize2, Minimize2, Users as UsersIcon, Eye, Code, Download, Cloud, RefreshCcw, FileSignature, Check, Plus, Search, Save, Link as LinkIcon } from 'lucide-react';
import CVContentSidebar from './CVContentSidebar';
import { generateHTMLFromCV } from '../utils/cvToHtml';
import SearchableCombobox from './SearchableCombobox';

interface DocumentBuilderProps {
    user: User | null;
    initialData?: {
        title: string;
        content: string;
        candidateId?: string;
        fileId?: string;
        vacancyId?: string;
        vacancyTitle?: string;
    } | null;
    initialFileId?: string;
    onBack: () => void;
    /** Vanuit «Voorgesteld»: koppel opgemaakt document aan klantportaal-voorstel. */
    portalProposalAttachMode?: boolean;
    onAttachPortalProposal?: (snapshot: Record<string, unknown>) => void;
}

// Initial empty CV data for when no candidate is selected
const defaultCVData: CVData = {
    title: 'Nieuw Document',
    summary: '',
    personalInfo: {
        name: '', email: '', phone: '', location: '', address: '', zipCode: '', city: '', country: '', linkedin: ''
    },
    workHistory: [],
    education: [],
    skills: [],
    hobbies: []
};

// Helper: removed renderTemplateApproximation as we now use real Collabora live preview.


const DocumentBuilder: React.FC<DocumentBuilderProps> = ({ user, initialData, initialFileId, onBack, portalProposalAttachMode, onAttachPortalProposal }) => {
    const [templates, setTemplates] = useState<any[]>([]);
    const [selectedTemplate, setSelectedTemplate] = useState<any | null>(null);
    const [isLoadingTemplates, setIsLoadingTemplates] = useState(true);
    
    const [candidates, setCandidates] = useState<any[]>([]);
    const [selectedCandidateId, setSelectedCandidateId] = useState<string>('');
    const [vacancies, setVacancies] = useState<any[]>([]);
    const [selectedVacancyId, setSelectedVacancyId] = useState<string>('');
    const [resumeData, setResumeData] = useState<CVData>(defaultCVData);
    const [foundTags, setFoundTags] = useState<string[]>([]);
    
    // UI State
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [showRawShortcodes, setShowRawShortcodes] = useState(false);
    
    // Live Preview State (debounced)
    const [debouncedData, setDebouncedData] = useState<CVData>(defaultCVData);
    const [isGeneratingDoc, setIsGeneratingDoc] = useState(false);
    const [previewPdfUrl, setPreviewPdfUrl] = useState<string | null>(null);
    const [previewError, setPreviewError] = useState<string | null>(null);
    
    // Sidebar active section
    const [activeSection, setActiveSection] = useState<string>('personal_info');
    const [pages, setPages] = useState<CVSection[][]>([[]]);

    // Save State
    const [documentName, setDocumentName] = useState<string>('Nieuw Document');
    const [savedDocumentId, setSavedDocumentId] = useState<string | null>(null);
    const [isSaving, setIsSaving] = useState(false);
    const [isLoadedFromDraft, setIsLoadedFromDraft] = useState(false);

    /**
     * Laatste OneDrive-upload + koppeling aan het gegenereerde bestand (één storage_path + één dossier-id).
     * `documentFileId` === rij in `document_files` van generate-cv; zelfde uuid voor `documents` upsert.
     */
    const [pendingOneDriveSync, setPendingOneDriveSync] = useState<{
        itemId: string;
        fileName: string;
        canonicalStoragePath?: string;
        documentFileId?: string;
    } | null>(null);
    const [isSyncingFromOneDrive, setIsSyncingFromOneDrive] = useState(false);
    /** Na bewerken in Word: bij terugkeren naar dit tabblad automatisch ophalen en als kandidaat-document opslaan. */
    const [autoSyncOneDriveOnReturn, setAutoSyncOneDriveOnReturn] = useState(true);
    const hiddenSinceRef = useRef<number | null>(null);
    const visibilitySyncTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const lastAutoOneDriveSyncRef = useRef(0);
    const oneDriveSyncInFlightRef = useRef(false);

    // Parse initialData if present
    useEffect(() => {
        if (initialData && initialData.content) {
            try {
                const parsed = JSON.parse(initialData.content);
                if (parsed._isDocumentBuilder) {
                    setIsLoadedFromDraft(true);
                    setResumeData(parsed.resumeData);
                    setDocumentName(initialData.title);
                    setSavedDocumentId(initialData.fileId || initialFileId || null);
                    if (initialData.candidateId) setSelectedCandidateId(initialData.candidateId);
                    
                    if (parsed.templateId && templates.length > 0) {
                        const tmpl = templates.find(t => t.id === parsed.templateId);
                        if (tmpl) setSelectedTemplate(tmpl);
                    }
                }
            } catch(e) {}
        }
    }, [initialData, templates, initialFileId]);


    // Load templates and candidates on mount
    useEffect(() => {
        const loadInitialData = async () => {
            if (!user) return;
            setIsLoadingTemplates(true);
            try {
                // Fetch Word (.docx) templates
                const templatesData = await supabase_get_pro_cv_templates(user.id);
                if (templatesData) setTemplates(templatesData);

                // Fetch Recent Candidates for the dropdown (Limit to 50 for speed, users can search for more)
                const filterId = (user?.role === UserRole.CLIENT || user?.role === UserRole.CLIENT_RECRUITER) ? user.clientId : undefined;
                const recentTalentPool = await supabase_get_paginated_talent_pool(filterId, 1, 50, '');
                if (recentTalentPool && recentTalentPool.data) {
                    const extractedCandidates = recentTalentPool.data.map((tp: any) => tp.candidate).filter(Boolean);
                    const uniqueCandidates = Array.from(new Map(extractedCandidates.map((c: any) => [c.id, c])).values());
                    setCandidates(uniqueCandidates);
                }

                // Fetch Vacancies
                const vacanciesData = await supabase_get_vacancies(filterId);
                if (vacanciesData) {
                    // Force uniqueness to prevent React key duplication warnings
                    const uniqueVacancies = Array.from(new Map(vacanciesData.map((v: any) => [v.id, v])).values());
                    setVacancies(uniqueVacancies);
                }

            } catch (err) {
                console.error("Error loading builder data:", err);
            } finally {
                setIsLoadingTemplates(false);
            }
        };

        loadInitialData();
    }, [user]);

    // Auto-select first template if none is selected
    useEffect(() => {
        if (!selectedTemplate && templates.length > 0) {
            setSelectedTemplate(templates[0]);
        }
    }, [templates, selectedTemplate]);

    /** Bij voorstel-flow: vacature uit navigatie-context voorzien in builder. */
    useEffect(() => {
        const vid = String(initialData?.vacancyId || '').trim();
        if (!vid || !vacancies.length) return;
        if (vacancies.some((v: any) => v.id === vid)) {
            setSelectedVacancyId(vid);
        }
    }, [initialData?.vacancyId, vacancies]);

    // Initialize/Sync CVData when candidate changes
    useEffect(() => {
        const fetchCandidateData = async () => {
            if (!selectedCandidateId) {
                if (!isLoadedFromDraft) setResumeData(defaultCVData);
                return;
            }
            if (isLoadedFromDraft) {
                // Do not override draft data with fresh candidate data, 
                // but allow subsequent template syncs if needed
                return; 
            }
            
            try {
                if (!user?.clientId) return;

                const profile = await supabase_get_candidate_profile_by_id(selectedCandidateId, user.clientId);
                const trueCandidate = (profile as any)?.candidates || profile?.candidate;
                if (!trueCandidate) throw new Error("Kandidaat niet gevonden of geen toegang.");

                const candidateRef = trueCandidate;
                const profileData = profile;

                // Extract Custom Fields from Template Structure
                let templateCustomFields: any[] = [];
                if (selectedTemplate?.structure) {
                     selectedTemplate.structure.forEach((page: any[]) => {
                         page.forEach((sec: any) => {
                             if (sec.type === 'custom_list' || sec.type === 'custom') {
                                 templateCustomFields.push({ id: sec.id, name: sec.title || 'Nieuw Veld', value: '' });
                             }
                         });
                     });
                }

                // Match TalentPool mapping
                const rawData = (profileData as any)?.parsed_cv || profileData?.ai_parsed_data || {};

                // Extract Strings safely
                const extractStrings = (val: any) => {
                    if (!val) return [];
                    if (typeof val === 'string') return val.split(',').map(s => s.trim()).filter(Boolean);
                    if (Array.isArray(val)) return val.map(a => typeof a === 'string' ? a : (a.name || a.skilldetail || a.skill || '')).filter(Boolean);
                    return [];
                };
                
                const allSkills = [...extractStrings(rawData.skills), ...extractStrings(rawData.tools), ...extractStrings(rawData.tags), ...extractStrings(candidateRef.tags), ...extractStrings(candidateRef.skills)];
                const combinedSkills = [...new Set(allSkills)].map(s => ({ name: s, skilldetail: s, skill: s }));

                // Map Education to ensure 'year' is extracted from 'period' if needed
                const getYearFallback = (item: any) => item.year || item.jaar || item.period || item.periode || item.date || item.courseyear || item.educationyear || item.experienceyear || (item.startDate ? `${item.startDate} - ${item.endDate || 'Heden'}` : '') || item.start_date || item.end_date || '';

                const extractYearFromString = (text: string) => {
                    const match = text.match(/\b((?:19|20)\d{2})(?:\s*[-tot/tm]+\s*((?:19|20)\d{2}|Heden|heden|Now|now))?\b/);
                    if (match) return { year: match[0], rest: text.replace(match[0], '').replace(/^[-,\s]+|[-,\s]+$/g, '') };
                    return { year: '', rest: text };
                };

                const rawEducations = Array.isArray(rawData.education) ? rawData.education : Array.isArray(rawData.educations) ? rawData.educations : Array.isArray(rawData.opleidingen) ? rawData.opleidingen : Array.isArray(candidateRef.education) ? candidateRef.education : [];
                const mappedEducation = rawEducations.map(e => {
                    if (typeof e === 'string') {
                        const { year, rest } = extractYearFromString(e);
                        return { school: '', degree: rest, year: year, educationyear: year, educationschool: '', education: rest };
                    }
                    const yearFallback = getYearFallback(e);
                    return {
                        school: e.school || e.institute || e.name || '',
                        degree: e.degree || e.education || e.title || '',
                        year: yearFallback,
                        educationyear: yearFallback,
                        educationschool: e.school || e.institute || e.name || '',
                        education: e.degree || e.education || e.title || ''
                    };
                });

                // Map Courses
                const rawCourses = Array.isArray(rawData.courses) ? rawData.courses : Array.isArray(rawData.certifications) ? rawData.certifications : Array.isArray(rawData.trainingen) ? rawData.trainingen : Array.isArray(candidateRef.courses) ? candidateRef.courses : [];
                const mappedCourses = rawCourses.map(c => {
                    if (typeof c === 'string') {
                        const { year, rest } = extractYearFromString(c);
                        return { coursetitel: rest, courseinstitute: '', courseyear: year };
                    }
                    return {
                        coursetitel: c.coursetitel || c.name || c.title || c.course || '',
                        courseinstitute: c.courseinstitute || c.institute || c.school || c.instituut || '',
                        courseyear: getYearFallback(c)
                    };
                });

                const rawTimeline = rawData.timeline || rawData.workHistory || rawData.work_experience || rawData.experience || rawData.werkervaring || candidateRef.timeline || [];
                const mappedWorkHistory = rawTimeline.map((t: any) => {
                    if (typeof t === 'string') {
                        const { year, rest } = extractYearFromString(t);
                        return { company: '', role: rest, period: year, description: '' };
                    }
                    return { company: t.company, role: t.role || t.title, period: getYearFallback(t), description: typeof t.description === 'string' ? t.description : '' };
                });

                setResumeData({
                    candidateId: candidateRef.id,
                    title: `Document - ${candidateRef.name}`,
                    summary: rawData.summary || candidateRef.cvSummary || candidateRef.summary || '',
                    motivation: rawData.motivation || candidateRef.motivation || '',
                    personalInfo: {
                        name: candidateRef.name || '',
                        email: candidateRef.email || '',
                        phone: candidateRef.phone || '',
                        location: candidateRef.location || '',
                        address: candidateRef.address || '',
                        zipCode: candidateRef.zipCode || '',
                        city: candidateRef.city || '',
                        country: candidateRef.country || '',
                        linkedin: candidateRef.linkedinUrl || ''
                    },
                    workHistory: mappedWorkHistory,
                    education: mappedEducation,
                    course: mappedCourses,
                    skills: combinedSkills,
                    hobbies: rawData.hobbies || [],
                    customFields: templateCustomFields,
                    eisen: (rawData.eisen || candidateRef.eisen || []).map((e: any) => typeof e === 'string' ? { eistitel: e, eistekst: '' } : e),
                    wensen: (rawData.wensen || candidateRef.wensen || []).map((w: any) => typeof w === 'string' ? { wenstitel: w, wenstekst: '' } : w)
                });
            } catch (err) {
                console.error("Error fetching candidate detailed data for Document Builder:", err);
            }
        };
        fetchCandidateData();
    }, [selectedCandidateId]);

    // Expose generated pages function, since we do manual generate
    const getGeneratedPages = (data: CVData): CVSection[][] => {
        const flatEisen = (data.eisen || []).map((e, idx) => ({ id: `eis${idx+1}`, type: 'custom_text' as const, content: { text: e, id: `eis${idx+1}` } }));
        const flatWensen = (data.wensen || []).map((w, idx) => ({ id: `wens${idx+1}`, type: 'custom_text' as const, content: { text: w, id: `wens${idx+1}` } }));
        const flatCustoms = (data.customFields || []).map(cf => ({ 
            id: `cf_${cf.id}`, 
            type: 'custom_text' as const, 
            content: { 
                text: cf.isList ? (cf.listValues || []).filter(Boolean).join('\n') : cf.value, 
                id: cf.tag || cf.name,
                descriptions: cf.isList 
                    ? (cf.listValues || []).filter(Boolean).map(v => ({ punt: v })) 
                    : [{ punt: (cf.value || '').replace(/<\/p>\s*<p>/gi, '\n').replace(/<br\s*\/?>/gi, '\n').replace(/<li[^>]*>/gi, '• ').replace(/<[^>]*>?/gm, '').trim() }]
            } 
        }));

        return [[
            { id: 'summary_gen', type: 'custom_text', content: { text: data.summary, id: 'summary' } },
            { id: 'motivation_gen', type: 'custom_text', content: { text: data.motivation, id: 'motivation' } },
            ...flatEisen,
            ...flatWensen,
            ...flatCustoms,
            { id: 'exp_gen', type: 'experience', content: { items: (data.workHistory || []).map(w => ({ 
                ...w, 
                description: w.isList 
                    ? (w.listValues || []).filter(Boolean).join('\n► ') 
                    : (w.description || '').replace(/<\/p>\s*<p>/gi, '\n').replace(/<br\s*\/?>/gi, '\n').replace(/<li[^>]*>/gi, '• ').replace(/<[^>]*>?/gm, '').trim(),
                descriptions: w.isList 
                    ? (w.listValues || []).filter(Boolean).map(v => ({ punt: v })) 
                    : [{ punt: (w.description || '').replace(/<\/p>\s*<p>/gi, '\n').replace(/<br\s*\/?>/gi, '\n').replace(/<li[^>]*>/gi, '• ').replace(/<[^>]*>?/gm, '').trim() }]
            })) } },
            { id: 'edu_gen', type: 'education', content: { items: data.education } },
            { id: 'skills_gen', type: 'skills', content: { items: data.skills } },
            { id: 'course_gen', type: 'course', content: { items: data.course } },
        ]];
    };

    // Generate Word Document silently for preview
    const handleGeneratePreview = async () => {
        if (!selectedTemplate?.id || !user?.id) return;
        setIsGeneratingDoc(true);
        try {
            // If "Raw Shortcodes" is heavily requested, we'd normally send empty data. 
            // But Docxtemplater might remove tags. Let's send empty data when raw is toggled 
            // and see if the template keeps them, or just let them see the real layout with some dummy tags.
            let generatedPages: CVSection[][] = [[]];
            
            if (!showRawShortcodes) {
                generatedPages = getGeneratedPages(resumeData);
            }

            const apiUrl = window.location.hostname === 'localhost' ? 'http://localhost:4000' : '';
            
            const response = await fetch(`${apiUrl}/api/documents/generate-cv`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    candidateId: selectedCandidateId || user.id,
                    templateId: selectedTemplate.id,
                    userId: user.id,
                    title: `Preview - ${resumeData.personalInfo.name || 'Template'}`,
                    pages: generatedPages,
                    liveResumeData: resumeData,
                    // If raw is on, we can pass a flag to stop backend Docxtemplater from erasing null tags
                    keepEmptyTags: showRawShortcodes 
                })
            });

            if (response.ok) {
                const docData = await response.json();
                
                if (docData.foundTags) {
                    setFoundTags(docData.foundTags);
                }

                if (!docData.pdfPublicUrl) {
                    setPreviewError(docData.pdfError || "Kon geen PDF preview genereren.");
                    setPreviewPdfUrl(null);
                    return;
                }

                setPreviewError(null);
                setPreviewPdfUrl(docData.pdfPublicUrl);
            } else {
                 let errText = await response.text();
                 try { errText = JSON.parse(errText).error || errText; } catch(e) {}
                 console.error("Server return error:", errText);
                 setPreviewError(`Server Fout: ${errText}`);
            }
        } catch (error: any) {
            console.error("Live preview generation failed:", error);
            setPreviewError(`Netwerkfout: ${error.message}`);
        } finally {
            setIsGeneratingDoc(false);
        }
    };

    const fetchTemplateTags = async () => {
        if (!selectedTemplate?.id) {
            return;
        }
        try {
            const apiUrl = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1' ? 'http://localhost:4000' : '';
            const response = await fetch(`${apiUrl}/api/documents/parse-template`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ templateId: selectedTemplate.id })
            });
            if (response.ok) {
                const data = await response.json();
                const newTags = data.foundTags || [];
                
                // Auto-add unknown tags to Custom Fields
                setResumeData(prev => {
                    const newCustoms = [...(prev.customFields || [])];
                    let changed = false;
                    
                    newTags.forEach((tag: string) => {
                        const cleanTag = tag.replace(/\[|\]/g, '');
                        if (cleanTag.startsWith('#') || cleanTag.startsWith('/')) return;
                        
                        const lowerTag = cleanTag.toLowerCase();
                        const isBuiltIn = ['name', 'voornaam', 'achternaam', 'email', 'phone', 'role', 'summary', 'motivation', 'address', 'zipcode', 'city', 'country', 'linkedin', 'skills', 'hobbies', 'company', 'companyname', 'role', 'period', 'experienceyear', 'description', 'skilldetail', 'educationschool', 'educationyear', 'coursetitel', 'courseinstitute', 'courseyear', 'eistitel', 'eistekst', 'wenstitel', 'wenstekst'].includes(lowerTag) || 
                                          /^(experience|education|skill|hobby|course|kennissoftware|werkervaring)(company|role|period|experienceyear|degree|school|year|institute)?\d+$/.test(lowerTag) ||
                                          /^(experience|education|skills|hobbies|courses|workhistory|eis|wens|course)$/.test(lowerTag);
                                          
                        if (!isBuiltIn) {
                            const exists = newCustoms.some(cf => (cf.tag || cf.name).toLowerCase() === lowerTag);
                            if (!exists) {
                                const prettyName = cleanTag.charAt(0).toUpperCase() + cleanTag.slice(1).replace(/[_-]/g, ' ');
                                newCustoms.push({ id: Date.now().toString() + Math.random(), name: prettyName, tag: cleanTag, value: '' });
                                changed = true;
                            }
                        }
                    });
                    if (changed) {
                        return { ...prev, customFields: newCustoms };
                    }
                    return prev;
                });
            }
        } catch (e) {
            console.error("Failed to parse template tags", e);
        }
    };

    const handleAddCustomField = (tagRaw: string) => {
        const cleanTag = tagRaw.replace(/\[|\]/g, '');
        const exists = resumeData.customFields?.some(cf => (cf.tag || cf.name).toLowerCase() === cleanTag.toLowerCase());
        
        if (!exists) {
            const prettyName = cleanTag.charAt(0).toUpperCase() + cleanTag.slice(1).replace(/[_-]/g, ' ');
            setResumeData(prev => ({
                ...prev,
                customFields: [
                    ...(prev.customFields || []),
                    { id: Date.now().toString(), name: prettyName, tag: cleanTag, value: '' }
                ]
            }));
        }
    };

    // Auto trigger initial preview when template changes
    useEffect(() => {
        if (selectedTemplate?.id) {
            handleGeneratePreview();
            fetchTemplateTags();
        }
    }, [selectedTemplate?.id, showRawShortcodes]);

    const handleSyncFromOneDrive = useCallback(async (mode: 'manual' | 'auto') => {
        if (!pendingOneDriveSync?.itemId) {
            if (mode === 'manual') {
                alert('Geen OneDrive-bestand om te synchroniseren. Open eerst via «Open in M365» met een gekoppeld Microsoft-account.');
            }
            return;
        }
        if (!user?.clientId) {
            if (mode === 'manual') {
                alert('Geen organisatie in je account — dit dossier kan niet automatisch worden bijgewerkt.');
            }
            return;
        }
        if (!selectedCandidateId) {
            if (mode === 'manual') {
                alert('Selecteer eerst een kandidaat; het bestand wordt dan bij Documenten van die kandidaat gezet.');
            }
            return;
        }
        if (oneDriveSyncInFlightRef.current) return;
        oneDriveSyncInFlightRef.current = true;
        setIsSyncingFromOneDrive(true);
        try {
            const { downloadOneDriveFileByItemId } = await import('../services/microsoftGraphService');
            const { blob, error: dlErr } = await downloadOneDriveFileByItemId(pendingOneDriveSync.itemId);
            if (dlErr || !blob) {
                throw dlErr instanceof Error ? dlErr : new Error((dlErr as Error)?.message || 'Download uit OneDrive mislukt.');
            }

            const useCanonical =
                Boolean(pendingOneDriveSync.canonicalStoragePath) && Boolean(pendingOneDriveSync.documentFileId);

            let storagePath: string;
            let documentsRowId: string;

            if (useCanonical) {
                storagePath = pendingOneDriveSync.canonicalStoragePath as string;
                documentsRowId = pendingOneDriveSync.documentFileId as string;
            } else {
                const safeName = (pendingOneDriveSync.fileName || 'document.docx').replace(/[^a-zA-Z0-9.\-_]/g, '_');
                documentsRowId = crypto.randomUUID();
                storagePath = `${user.clientId}/candidates/${selectedCandidateId}/${Date.now()}_${safeName}`;
            }

            const { error: uploadError } = await supabase.storage.from('documents').upload(storagePath, blob, {
                contentType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                upsert: true,
            });
            if (uploadError) throw uploadError;

            const { data: publicData } = supabase.storage.from('documents').getPublicUrl(storagePath);
            const nowIso = new Date().toISOString();

            await supabase_save_document({
                id: documentsRowId,
                client_id: user.clientId,
                title: pendingOneDriveSync.fileName,
                content: '',
                is_template: false,
                candidate_id: selectedCandidateId,
                file_url: publicData.publicUrl,
                storage_path: storagePath,
                updated_at: nowIso,
            });

            if (useCanonical && pendingOneDriveSync.documentFileId) {
                const { error: dfErr } = await supabase
                    .from('document_files')
                    .update({ updated_at: nowIso })
                    .eq('id', pendingOneDriveSync.documentFileId);
                if (dfErr) console.warn('[DocumentBuilder] document_files updated_at:', dfErr);
            }

            window.dispatchEvent(
                new CustomEvent('wt:toast', {
                    detail: {
                        message: useCanonical
                            ? mode === 'auto'
                                ? 'OneDrive-versie: het gegenereerde bestand in Werftools is bijgewerkt (zelfde dossier-link).'
                                : 'Het gegenereerde bestand in Werftools is overschreven met je OneDrive-versie; het dossier toont dezelfde entry.'
                            : mode === 'auto'
                              ? 'OneDrive-versie is automatisch toegevoegd bij de kandidaat onder Documenten.'
                              : 'Het bestand uit OneDrive staat nu bij deze kandidaat onder Documenten.',
                        type: 'success',
                    },
                })
            );
        } catch (e: any) {
            console.error('[DocumentBuilder] OneDrive sync:', e);
            if (mode === 'manual') {
                alert('Synchroniseren mislukt: ' + (e?.message || String(e)));
            } else {
                window.dispatchEvent(
                    new CustomEvent('wt:toast', {
                        detail: {
                            message:
                                'Automatisch synchroniseren vanuit OneDrive mislukt — gebruik de knop «OneDrive → dossier».',
                            type: 'error',
                        },
                    })
                );
            }
        } finally {
            oneDriveSyncInFlightRef.current = false;
            setIsSyncingFromOneDrive(false);
        }
    }, [pendingOneDriveSync, selectedCandidateId, user?.clientId]);

    useEffect(() => {
        const onVis = () => {
            if (document.visibilityState === 'hidden') {
                hiddenSinceRef.current = Date.now();
                return;
            }
            if (!autoSyncOneDriveOnReturn) return;
            if (!pendingOneDriveSync?.itemId || !selectedCandidateId || !user?.clientId) return;

            const hs = hiddenSinceRef.current;
            hiddenSinceRef.current = null;
            if (hs == null) return;

            const awayMs = Date.now() - hs;
            if (awayMs < 4000) return;
            const now = Date.now();
            if (now - lastAutoOneDriveSyncRef.current < 45000) return;

            if (visibilitySyncTimerRef.current) clearTimeout(visibilitySyncTimerRef.current);
            visibilitySyncTimerRef.current = setTimeout(() => {
                visibilitySyncTimerRef.current = null;
                lastAutoOneDriveSyncRef.current = Date.now();
                void handleSyncFromOneDrive('auto');
            }, 2000);
        };

        document.addEventListener('visibilitychange', onVis);
        return () => {
            document.removeEventListener('visibilitychange', onVis);
            if (visibilitySyncTimerRef.current) clearTimeout(visibilitySyncTimerRef.current);
        };
    }, [autoSyncOneDriveOnReturn, pendingOneDriveSync?.itemId, selectedCandidateId, user?.clientId, handleSyncFromOneDrive]);

    const handleGenerate = async (action: 'download' | 'm365') => {
        if (!selectedTemplate?.id || !user?.id) return;
        setIsGeneratingDoc(true);

        try {
            const apiUrl = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1' ? 'http://localhost:4000' : '';
            const generatedPages: CVSection[][] = getGeneratedPages(resumeData);
            const response = await fetch(`${apiUrl}/api/documents/generate-cv`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    candidateId: selectedCandidateId || user.id,
                    templateId: selectedTemplate.id,
                    userId: user.id,
                    title: `Document - ${resumeData.personalInfo.name || 'Template'}`,
                    pages: generatedPages,
                    liveResumeData: resumeData
                })
            });

            if (response.ok) {
                const docData = await response.json();
                if (docData.publicUrl) {
                    // Common filename logic
                    const candidateName = resumeData.personalInfo?.name || 'Kandidaat';
                    const vacancy = vacancies.find(v => v.id === selectedVacancyId);
                    const clientName = vacancy ? (Array.isArray(vacancy.clients) ? vacancy.clients[0]?.name : (vacancy.clients?.name || (Array.isArray(vacancy.client) ? vacancy.client[0]?.name : vacancy.client?.name) || vacancy.data?.organizationText)) : null;
                    const rawTitle = clientName ? `CV ${candidateName} - ${clientName}` : `CV ${candidateName}`;
                    const safeTitle = rawTitle.replace(/[^a-zA-Z0-9 -_]/g, '').trim();
                    const downloadUrl = `${docData.publicUrl}?download=${encodeURIComponent(safeTitle)}.docx`;

                    if (action === 'download') {
                        const link = document.createElement('a');
                        link.href = downloadUrl;
                        link.setAttribute('download', `${safeTitle}.docx`);
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                    } else if (action === 'm365') {
                        setPendingOneDriveSync(null);
                        const fileName = `${(safeTitle.replace(/[/\\?%*:|"<>]/g, '-') || 'Document').slice(0, 180)}.docx`;
                        let openedViaOneDrive = false;
                        try {
                            const fileRes = await fetch(docData.publicUrl);
                            if (fileRes.ok) {
                                const blob = await fileRes.blob();
                                const { uploadToOneDrive } = await import('../services/microsoftGraphService');
                                const { webUrl, itemId, error: uploadErr } = await uploadToOneDrive(
                                    blob,
                                    fileName,
                                    'Kandidaten CVs',
                                    'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
                                );
                                if (webUrl && !uploadErr) {
                                    window.open(webUrl, '_blank', 'noopener,noreferrer');
                                    openedViaOneDrive = true;
                                    if (itemId) {
                                        const canonicalStoragePath =
                                            typeof docData.storage_path === 'string' && docData.storage_path.trim()
                                                ? docData.storage_path.trim()
                                                : undefined;
                                        const documentFileId =
                                            docData.id != null && String(docData.id).length > 0
                                                ? String(docData.id)
                                                : undefined;
                                        setPendingOneDriveSync({
                                            itemId,
                                            fileName,
                                            ...(canonicalStoragePath && documentFileId
                                                ? { canonicalStoragePath, documentFileId }
                                                : {}),
                                        });
                                        const hasCanonical = Boolean(canonicalStoragePath && documentFileId);
                                        window.dispatchEvent(
                                            new CustomEvent('wt:toast', {
                                                detail: {
                                                    message: hasCanonical
                                                        ? 'Geopend in OneDrive. Sync overschrijft hetzelfde gegenereerde bestand in Werftools en dezelfde dossier-entry (na Word: auto-sync of «OneDrive → dossier»).'
                                                        : 'Geopend in OneDrive. Na bewerken: terug naar dit tabblad voor automatische sync (vinkje «Auto na Word») of tik op «OneDrive → dossier» (elke sync wordt dan een apart dossierbestand).',
                                                    type: 'info',
                                                },
                                            })
                                        );
                                    } else {
                                        window.dispatchEvent(
                                            new CustomEvent('wt:toast', {
                                                detail: {
                                                    message:
                                                        'Geopend in OneDrive. Automatisch terugzetten in Werftools is nu niet beschikbaar voor dit bestand — download desnoods uit OneDrive en upload bij de kandidaat.',
                                                    type: 'info',
                                                },
                                            })
                                        );
                                    }
                                }
                            }
                        } catch (err) {
                            console.warn('[DocumentBuilder] OneDrive open failed:', err);
                        }
                        if (!openedViaOneDrive) {
                            const m365Url = `https://view.officeapps.live.com/op/edit.aspx?src=${encodeURIComponent(docData.publicUrl)}`;
                            window.open(m365Url, '_blank', 'noopener,noreferrer');
                        }
                    }
                } else {
                    alert("Kon document link niet maken, controleer of document storage public is.");
                }
            } else {
                 let errText = await response.text();
                 try { errText = JSON.parse(errText).error || errText; } catch(e) {}
                 alert(`Genereren mislukt op server: ${errText}`);
            }
        } catch (error: any) {
            console.error("Generate failed:", error);
            alert(`Netwerkfout bij genereren: ${error.message}`);
        } finally {
            setIsGeneratingDoc(false);
        }
    };

    const handleSaveDraft = async (asNew: boolean = false) => {
        if (!user?.clientId) return;
        if (!documentName.trim()) { alert("Vul een documentnaam in."); return; }
        setIsSaving(true);
        try {
            // using statically imported supabase_save_document
            
            const payloadContent = JSON.stringify({
                _isDocumentBuilder: true,
                templateId: selectedTemplate?.id,
                resumeData
            });

            const docId = (asNew || !savedDocumentId) ? crypto.randomUUID() : savedDocumentId;

            const docPayload = {
                id: docId,
                client_id: user.clientId,
                title: documentName,
                author_name: user?.name || 'Onbekend',
                content: payloadContent,
                updated_at: new Date().toISOString(),
                status: 'Draft' as any,
                is_template: false,
                candidate_id: selectedCandidateId || undefined
            };

            const saved = await supabase_save_document(docPayload);
            if (saved) {
                setSavedDocumentId(saved.id);
                // Force URL history without pushing so it doesn't break back stack natively
                alert(asNew ? "Document opgeslagen als nieuw!" : "Document opgeslagen!");
            }
        } catch(e) {
            console.error(e);
            alert("Fout bij opslaan document.");
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div className="flex flex-col h-[calc(100vh-80px)] xl:h-full bg-slate-50 relative animate-fadeIn overflow-hidden">
            {/* Header Toolbar */}
            <div className="bg-white border-b border-slate-200 px-6 py-3 flex items-center justify-between shrink-0 z-10">
                <div className="flex min-w-0 flex-1 flex-wrap items-center gap-3">
                    {portalProposalAttachMode ? (
                        <>
                            <button
                                type="button"
                                onClick={onBack}
                                className="flex shrink-0 items-center gap-2 rounded-xl border-2 border-slate-200 bg-white px-3 py-2 text-xs font-black uppercase tracking-wide text-slate-800 shadow-sm transition hover:border-indigo-300 hover:text-indigo-800"
                            >
                                <ChevronLeft size={18} />
                                Terug naar voorstel
                            </button>
                            {onAttachPortalProposal ? (
                                <button
                                    type="button"
                                    onClick={() => {
                                        onAttachPortalProposal({
                                            version: 2,
                                            source: 'documentbuilder',
                                            attachedAt: new Date().toISOString(),
                                            resumeData: JSON.parse(JSON.stringify(resumeData)),
                                            templateId: selectedTemplate?.id ?? null,
                                            documentName,
                                            candidateId: selectedCandidateId || resumeData?.candidateId || null,
                                        });
                                    }}
                                    className="flex shrink-0 items-center gap-2 rounded-xl border-2 border-emerald-500 bg-emerald-600 px-3 py-2 text-xs font-black uppercase tracking-wide text-white shadow-md transition hover:bg-emerald-700"
                                >
                                    <LinkIcon size={18} />
                                    Koppel aan voorstel
                                </button>
                            ) : null}
                        </>
                    ) : (
                        <button onClick={onBack} className="p-2 hover:bg-slate-100 rounded-lg text-slate-500 hover:text-slate-900 transition-colors">
                            <ChevronLeft size={20} />
                        </button>
                    )}
                    <div>
                        <div className="flex items-center gap-2 -ml-1">
                            <FileText className="text-[#185ABD] shrink-0" size={20} />
                            <input 
                                type="text"
                                value={documentName}
                                onChange={e => setDocumentName(e.target.value)}
                                className="font-bold text-lg text-slate-900 outline-none placeholder:text-slate-400 bg-transparent min-w-[250px] focus:bg-slate-100 px-1 py-0.5 rounded transition-colors"
                                placeholder="Typ documentnaam..."
                            />
                        </div>
                        <p className="text-[10px] text-slate-500 font-bold uppercase tracking-widest flex items-center gap-1 mt-0.5 ml-1">
                            {isSaving ? 'Opslaan...' : 'Bewerk en Genereer M365'}
                            {selectedCandidateId && <span className="bg-emerald-50 text-emerald-600 px-2 py-0.5 rounded ml-2">Gekoppeld aan Kandidaat</span>}
                        </p>
                        {portalProposalAttachMode && initialData?.vacancyTitle ? (
                            <p className="mt-1 max-w-xl text-[11px] font-semibold leading-snug text-slate-600">
                                Vacature: <span className="text-slate-900">{initialData.vacancyTitle}</span>
                            </p>
                        ) : null}
                    </div>
                </div>
                
                <div className="flex items-center gap-2">
                    <button onClick={() => handleSaveDraft(false)} disabled={isSaving} className="px-3 py-2 bg-white border border-slate-200 text-slate-700 rounded-lg hover:bg-slate-50 text-xs font-bold flex items-center gap-2 shadow-sm transition-all disabled:opacity-50">
                        {isSaving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Opslaan
                    </button>
                    {(savedDocumentId || initialData) && (
                        <button onClick={() => handleSaveDraft(true)} disabled={isSaving} className="px-3 py-2 bg-slate-100 text-slate-600 rounded-lg hover:bg-slate-200 text-xs font-bold flex items-center gap-2 transition-all disabled:opacity-50 mr-2">
                            Opslaan als...
                        </button>
                    )}
                    {pendingOneDriveSync?.itemId && (
                        <div className="flex flex-wrap items-center gap-2 border-l border-slate-200 pl-2 ml-0 sm:ml-1">
                            <label className="flex items-center gap-1.5 text-[10px] font-bold text-slate-600 cursor-pointer whitespace-nowrap">
                                <input
                                    type="checkbox"
                                    checked={autoSyncOneDriveOnReturn}
                                    onChange={(e) => setAutoSyncOneDriveOnReturn(e.target.checked)}
                                    className="rounded border-slate-300 text-[#185ABD] focus:ring-[#185ABD]"
                                />
                                Auto na Word
                            </label>
                            <button
                                type="button"
                                onClick={() => void handleSyncFromOneDrive('manual')}
                                disabled={isSyncingFromOneDrive || !selectedCandidateId || !user?.clientId}
                                title={
                                    !user?.clientId
                                        ? 'Geen organisatie-context'
                                        : !selectedCandidateId
                                          ? 'Selecteer eerst een kandidaat'
                                          : 'Haal de laatste versie uit OneDrive en zet die bij Documenten'
                                }
                                className="flex items-center gap-1.5 px-2.5 py-2 rounded-lg text-[10px] font-bold bg-emerald-50 text-emerald-800 border border-emerald-200 hover:bg-emerald-100 disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                                {isSyncingFromOneDrive ? <Loader2 size={14} className="animate-spin shrink-0" /> : <Cloud size={14} className="shrink-0" />}
                                <span className="hidden sm:inline">OneDrive → dossier</span>
                                <span className="sm:hidden">Sync</span>
                            </button>
                        </div>
                    )}
                    {/* Template Selector */}
                    <select 
                        className="bg-slate-50 border border-slate-200 text-slate-700 text-sm font-bold rounded-lg px-4 py-2.5 outline-none appearance-none min-w-[200px]"
                        value={selectedTemplate?.id || ''}
                        onChange={(e) => {
                            const tmpl = templates.find(t => t.id === e.target.value);
                            setSelectedTemplate(tmpl || null);
                        }}
                        disabled={isLoadingTemplates}
                    >
                        <option value="">Selecteer Pro Template...</option>
                        {templates.map(t => (
                            <option key={t.id} value={t.id}>{t.title || t.name}</option>
                        ))}
                    </select>
                </div>
            </div>

            {/* Split Screen Layout */}
            <div className={`flex flex-col lg:flex-row flex-1 overflow-hidden relative ${isFullscreen ? 'bg-slate-100' : ''}`}>
                
                {/* Left Pane: Editor */}
                <div className={`${isFullscreen ? 'hidden' : 'w-full lg:w-1/2 flex flex-col pt-0 bg-white lg:border-r border-b lg:border-b-0 border-slate-200 relative shadow-xl z-20 overflow-hidden shrink-0 h-[50vh] lg:h-auto'}`}>
                    
                    <div className="flex-1 overflow-hidden relative">
                        <div className="absolute inset-0 opacity-100 z-10 transition-opacity duration-300">
                            <CVContentSidebar 
                               data={resumeData}
                               onChange={setResumeData}
                               vacancy={vacancies.find(v => v.id === selectedVacancyId)}
                               vacancies={vacancies}
                               selectedVacancyId={selectedVacancyId}
                               onSelectVacancy={setSelectedVacancyId}
                               candidates={candidates}
                               selectedCandidateId={selectedCandidateId}
                               onSelectCandidate={setSelectedCandidateId}
                               showShortcodes={true}
                               foundTags={foundTags}
                               isLoadingCandidates={isLoadingTemplates}
                               isLoadingVacancies={isLoadingTemplates}
                               clientId={user?.role === 'ADMIN' ? undefined : (user?.clientId || user?.id)}
                               className="w-full h-full border-0 shadow-none overflow-y-auto custom-scrollbar"
                            />
                        </div>
                    </div>
                </div>

                {/* Right Pane: HTML Template Preview */}
                <div className={`${isFullscreen ? 'w-full' : 'w-full lg:w-1/2'} flex flex-col bg-slate-100 transition-all duration-300 relative h-[50vh] lg:h-auto`}>
                    
                    {/* Toolbar for Preview */}
                    <div className="bg-white border-b border-slate-200 flex flex-wrap items-center justify-between p-3 gap-3 shrink-0 z-10 shadow-sm">
                        
                        {/* Toggle Mode */}
                        <div className="flex items-center bg-slate-100 p-1 rounded-lg shrink-0">
                            <button
                                onClick={() => setShowRawShortcodes(true)}
                                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[10px] uppercase tracking-wider font-bold transition-all ${showRawShortcodes ? 'bg-white shadow text-[#185ABD]' : 'text-slate-500 hover:text-slate-700'}`}
                            >
                                <Code size={14} />
                                <span className="hidden sm:inline">Raw Shortcodes</span>
                                <span className="sm:hidden">Raw</span>
                            </button>
                            <button
                                onClick={() => setShowRawShortcodes(false)}
                                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[10px] uppercase tracking-wider font-bold transition-all ${!showRawShortcodes ? 'bg-white shadow text-[#185ABD]' : 'text-slate-500 hover:text-slate-700'}`}
                            >
                                <Eye size={14} />
                                <span className="hidden sm:inline">Gevulde Data</span>
                                <span className="sm:hidden">Preview</span>
                            </button>
                        </div>

                        <div className="flex flex-wrap items-center gap-2">
                            {isGeneratingDoc && (
                                <div className="hidden sm:flex items-center gap-2 text-xs font-bold text-slate-500 animate-pulse">
                                    <Loader2 size={14} className="animate-spin text-[#185ABD]" />
                                    Synchroniseren...
                                </div>
                            )}
                            <button 
                                onClick={handleGeneratePreview}
                                disabled={!selectedTemplate || isGeneratingDoc}
                                className="flex items-center gap-2 bg-slate-100 text-slate-700 px-3 py-2 rounded-lg text-xs font-bold shadow-sm hover:bg-slate-200 transition-all disabled:opacity-50 disabled:cursor-not-allowed hidden md:flex"
                            >
                                <RefreshCcw size={14} className={isGeneratingDoc ? "animate-spin" : ""} />
                                <span className="hidden xl:inline">{isGeneratingDoc ? "Wordt geladen..." : "Vernieuw Voorbeeld"}</span>
                            </button>
                            
                            <div className="flex bg-white rounded-lg border border-[#185ABD] overflow-hidden shadow-sm">
                                <button 
                                    onClick={() => handleGenerate('download')}
                                    disabled={!selectedTemplate || isGeneratingDoc}
                                    className="flex items-center justify-center gap-2 bg-[#185ABD] text-white px-3 md:px-4 py-2 text-xs md:text-sm font-bold hover:bg-[#144799] transition-all disabled:opacity-50 disabled:cursor-not-allowed group w-10 md:w-auto"
                                    title="Download Word"
                                >
                                    <Download size={14} />
                                    <span className="hidden md:inline">Download Word</span>
                                </button>
                                <div className="w-[1px] bg-blue-700"></div>
                                <button 
                                    onClick={() => handleGenerate('m365')}
                                    disabled={!selectedTemplate || isGeneratingDoc}
                                    className="flex items-center justify-center gap-2 bg-[#185ABD] text-white px-3 md:px-4 py-2 text-xs md:text-sm font-bold hover:bg-[#144799] transition-all disabled:opacity-50 disabled:cursor-not-allowed group w-10 md:w-auto"
                                    title="Open in Word (OneDrive) om te bewerken. Zonder Microsoft-koppeling: Word online (beperkt)."
                                >
                                    <FileSignature size={14} />
                                    <span className="hidden md:inline">Open in M365</span>
                                </button>
                            </div>
                            <button 
                                onClick={() => setIsFullscreen(!isFullscreen)} 
                                className="p-2 border border-slate-200 text-slate-500 hover:text-slate-900 hover:bg-slate-50 rounded-lg transition-colors bg-white shadow-sm hidden md:block"
                                title={isFullscreen ? "Afsluiten" : "Volledig scherm"}
                            >
                                {isFullscreen ? <Minimize2 size={18} /> : <Maximize2 size={18} />}
                            </button>
                        </div>
                    </div>

                    {/* Preview Content Area */}
                    <div className="flex-1 bg-slate-100 overflow-y-auto custom-scrollbar flex justify-center items-start relative">
                        {isGeneratingDoc ? (
                            <div className="absolute inset-0 flex items-center justify-center bg-white/80 backdrop-blur-sm z-10 animate-fadeIn">
                                <div className="flex flex-col items-center bg-white p-6 rounded-2xl shadow-xl border border-slate-100">
                                    <Loader2 className="h-10 w-10 text-[#185ABD] animate-spin mb-4" />
                                    <span className="text-slate-800 font-bold text-lg">Document renderen...</span>
                                    <span className="text-slate-500 text-sm mt-2 max-w-[250px] text-center leading-relaxed">Let op: afbeeldingen en headers kunnen de verwerking iets langer maken.</span>
                                </div>
                            </div>
                        ) : previewError ? (
                            <div className="absolute inset-0 flex items-center justify-center bg-white z-10 p-6">
                                <div className="bg-red-50 text-red-600 px-6 py-5 rounded-xl border border-red-200 font-medium flex flex-col items-center gap-3 text-center shadow-sm max-w-sm">
                                    <Cloud className="h-10 w-10 text-red-400 mb-2" />
                                    <p className="font-bold">Genereren Mislukt</p>
                                    <p className="text-sm text-red-500 opacity-90">{previewError}</p>
                                </div>
                            </div>
                        ) : previewPdfUrl ? (
                            <div className="w-full h-full flex justify-center animate-fadeIn">
                                <iframe 
                                    src={`${previewPdfUrl}#view=FitH&navpanes=0&toolbar=0`} 
                                    className="w-full h-full border-0 bg-transparent" 
                                    title="Live PDF Preview"
                                />
                            </div>
                        ) : selectedTemplate ? (
                             <div className="flex flex-col items-center justify-center h-full text-slate-400 w-full absolute inset-0">
                                <FileText size={48} className="mb-4 opacity-50 text-[#185ABD]" />
                                <p className="text-sm font-bold uppercase tracking-widest text-[#185ABD]">Voorbereiden...</p>
                                <p className="text-xs mt-2 max-w-sm text-center">We laden de preview in.</p>
                            </div>
                        ) : (
                            <div className="flex flex-col items-center justify-center h-full text-slate-400 w-full absolute inset-0">
                                <FileText size={48} className="mb-4 opacity-50 text-[#185ABD]" />
                                <p className="text-sm font-bold uppercase tracking-widest">Selecteer een Word Template</p>
                                <p className="text-xs mt-2 max-w-sm text-center">De lay-out wordt lokaal gegenereerd na selectie.</p>
                            </div>
                        )}
                    </div>
                </div>

            </div>
        </div>
    );
};

export default DocumentBuilder;
